import type { MoodId } from '../components/crystalMoodData';
import type { CrystalIconKey } from '../components/CrystalIcons';

export const COPING_STRATEGIES = [
  'Heat',
  'Ice',
  'Rest',
  'Hydration',
  'Stretching/Movement',
  'Breathing/Grounding',
  'Medication',
  'Hospital visit',
  'Hot bath/shower',
  'Massage',
  'Gentle walk',
  'Sleep/nap',
  'Distraction (TV/music)',
  'Journaling',
  'Crying it out',
  'Talking to someone',
  'Validation/being heard',
  'Therapy/counselling',
  'Prayer/spirituality',
  'Community support group',
  'Peer support',
  'Nature/fresh air',
  'Self-advocacy (speaking up)',
  'Following my care plan',
  'Nothing helped yet',
  'Other',
] as const;

export const PROTECTIVE_FACTORS = [
  'Support person with me', 'I spoke up for myself', 'A provider listened', 'Family support',
  'Faith/community', 'Grounding/breathing helped', 'Used my script or care plan',
  'Community/peer support', 'I rested after', 'Humour / lightness helped',
] as const;

export const EXPERIENCE_TAGS = [
  'Drug-seeking accusation', 'Race/Racism', 'Security called/watched', 'Age/Too young',
  'Long wait after others', 'Medication refused', 'Dismissed or not listened to',
  'Feeling threatened', 'Positive support', 'Patient advocate helped',
] as const;

export const EXPERIENCE_TAG_GROUPS = {
  'Racism & ethnicity': [
    { code: 'racial-profiling', label: 'Racial profiling or suspicion' },
    { code: 'racial-slur', label: 'Racial slurs or comments' },
    { code: 'treated-differently-race', label: 'Treated differently because of race' },
    { code: 'cultural-dismissal', label: 'Culture or traditions dismissed' },
    { code: 'language-barrier', label: 'Language used against me' },
    { code: 'immigration-status', label: 'Immigration status questioned or used against me' },
  ],
  'Ableism & disease': [
    { code: 'dont-look-sick', label: '"You don\'t look sick"' },
    { code: 'condition-doubted', label: 'My condition was doubted' },
    { code: 'disability-ignored', label: 'Disability or needs ignored' },
    { code: 'pity-talked-down', label: 'Pitied or talked down to' },
    { code: 'outed-condition', label: 'Condition disclosed without my consent' },
    { code: 'accommodations-denied', label: 'Accommodations refused' },
  ],
  'Pain-related': [
    { code: 'drug-seeking', label: 'Called or treated as drug-seeking' },
    { code: 'pain-dismissed', label: 'Pain dismissed or minimized' },
    { code: 'medication-refused', label: 'Medication refused or delayed' },
    { code: 'pain-exaggerating', label: 'Accused of exaggerating pain' },
    { code: 'pain-compared', label: 'Pain compared to others\'' },
  ],
  'Gender, sexuality & identity': [
    { code: 'misgendered', label: 'Misgendered or deadnamed' },
    { code: 'homophobia', label: 'Homophobic comments or treatment' },
    { code: 'transphobia', label: 'Transphobic comments or treatment' },
    { code: 'gender-dismissed', label: 'Gender used to dismiss concerns' },
    { code: 'sexuality-assumed', label: 'Sexuality assumed or questioned' },
  ],
  'Healthcare-specific': [
    { code: 'long-wait', label: 'Made to wait longer than others' },
    { code: 'not-listened', label: 'Not listened to or dismissed' },
    { code: 'security-involved', label: 'Security called or watched' },
    { code: 'denied-service', label: 'Denied care or service' },
    { code: 'no-scd-knowledge', label: 'Provider didn\'t understand SCD' },
    { code: 'forced-protocol', label: 'Forced into wrong protocol' },
    { code: 'consent-violated', label: 'Consent not asked or ignored' },
  ],
  'Social & structural': [
    { code: 'excluded', label: 'Excluded or left out' },
    { code: 'different-rules', label: 'Different rules applied to me' },
    { code: 'threatened', label: 'Felt physically threatened' },
    { code: 'verbal-abuse', label: 'Verbal abuse or harassment' },
    { code: 'watched-followed', label: 'Watched or followed (store, public)' },
    { code: 'income-class', label: 'Treated differently for income or class' },
    { code: 'age-dismissed', label: 'Age used to dismiss me' },
  ],
  'Positive experiences': [
    { code: 'positive-support', label: 'Received genuine support' },
    { code: 'advocate-helped', label: 'Patient advocate or ally helped' },
    { code: 'felt-seen', label: 'Felt truly seen and heard' },
    { code: 'community-support', label: 'Community had my back' },
    { code: 'family-understood', label: 'Family understood' },
  ],
} as const;

export const WATERS_CHECK_OPTIONS = [
  { code: 'calm', label: 'Calm waters', desc: 'Feeling steady today' },
  { code: 'choppy', label: 'Choppy', desc: 'Some waves but I\'m afloat' },
  { code: 'rough', label: 'Rough seas', desc: 'It\'s heavy out here' },
  { code: 'sos', label: 'SOS', desc: 'I need support right now' },
] as const;

export const STRESS_DESCRIPTOR_CHIPS = [
  { code: 'work-school', label: '💼 Work / school' },
  { code: 'health-worry', label: '🏥 Health worries' },
  { code: 'money', label: '💰 Money / finances' },
  { code: 'relationships', label: '💬 Relationships' },
  { code: 'pain-related', label: '🩸 Pain-related' },
  { code: 'treatment', label: '💊 Treatment / medication' },
  { code: 'discrimination', label: '✊ Discrimination' },
  { code: 'general', label: '🌀 General overwhelm' },
] as const;

// Chip selectors are text-only for now (2026-05-21 revert). The Phase 1
// Citrine moodId / facesPosition wiring was reverted from the chip data;
// new symbol designs are coming in a follow-up phase. The MoodChip
// interface keeps moodId / facesPosition as optional fields so the dev
// preview surface (dev/CrystalMoodsPreview.tsx) and the FACES slider
// (SliderField + SCALE_MOOD_IDS) continue to typecheck and so a future
// phase can re-introduce typed mappings without another schema change.
export interface MoodChip {
  code: string;
  label: string;
  moodId?: MoodId;
  facesPosition?: 0 | 2 | 4 | 6 | 8 | 10;
  iconKey?: CrystalIconKey;
}

export const FEELING_CHIPS: readonly MoodChip[] = [
  { code: 'okay',       label: 'Okay' },
  { code: 'low',        label: 'Low',         iconKey: 'mood-low' },
  { code: 'anxious',    label: 'Anxious',     iconKey: 'mood-anxious' },
  { code: 'frustrated', label: 'Frustrated' },
  { code: 'numb',       label: 'Numb' },
  { code: 'hopeful',    label: 'Hopeful',     iconKey: 'mood-hopeful' },
  { code: 'grateful',   label: 'Grateful' },
  { code: 'angry',      label: 'Angry' },
  { code: 'calm',       label: 'Calm',        iconKey: 'mood-calm' },
  { code: 'lonely',     label: 'Lonely',      iconKey: 'mood-lonely' },
  { code: 'happy',      label: 'Happy' },
  { code: 'pain-tired', label: 'Pain-tired' },
  { code: 'warrior',    label: 'Warrior mode', iconKey: 'mood-warrior' },
] as const;

export const BODY_SENSATION_CHIPS = {
  comfortable: [
    { code: 'warm', label: '☀️ Warm' },
    { code: 'relaxed', label: '😌 Relaxed' },
    { code: 'energised', label: '⚡ Energised' },
    { code: 'grounded', label: '🌱 Grounded' },
    { code: 'calm', label: '🌊 Calm' },
    { code: 'light', label: '🪶 Light' },
  ],
  uncomfortable: [
    { code: 'tight', label: '🪢 Tight' },
    { code: 'heavy', label: '🪨 Heavy' },
    { code: 'buzzing', label: '🐝 Buzzing/restless' },
    { code: 'numb', label: '🧊 Numb/shut down' },
    { code: 'achy', label: '🌧 Achy' },
    { code: 'shaky', label: '🌀 Shaky/trembling' },
    { code: 'hot', label: '🔥 Hot/feverish' },
    { code: 'cold', label: '❄️ Cold/chills' },
    { code: 'nauseous', label: '🤢 Nauseous' },
    { code: 'dizzy', label: '💫 Dizzy' },
    { code: 'breathless', label: '🫁 Breathless' },
    { code: 'swollen', label: '🫧 Swollen/puffy' },
  ],
} as const;

export const SLEEP_QUALITY_CHIPS = [
  { code: 'trouble-falling', label: '🛏 Trouble falling asleep' },
  { code: 'woke-up-lots', label: '⏰ Woke up a lot' },
  { code: 'pain-woke-me', label: '🩸 Pain woke me' },
  { code: 'nightmares', label: '😱 Nightmares' },
  { code: 'too-hot-cold', label: '🌡 Too hot / cold' },
  { code: 'slept-well', label: '🌙 Actually slept well' },
] as const;

export const WORRY_CHIPS = [
  { code: 'health', label: '🏥 Health / symptoms' },
  { code: 'future', label: '🔮 Future worries' },
  { code: 'school-work', label: '📚 School / work' },
  { code: 'social', label: '👥 Social situations' },
  { code: 'money', label: '💰 Money / finances' },
  { code: 'racing-thoughts', label: '🌀 Racing thoughts' },
  { code: 'what-ifs', label: '❓ What-ifs' },
] as const;

export const FEELING_BLUE_CHIPS = [
  { code: 'lonely', label: '🫥 Lonely' },
  { code: 'hopeless', label: '🕳 Hopeless' },
  { code: 'unmotivated', label: '😶 No motivation' },
  { code: 'tearful', label: '💧 Tearful' },
  { code: 'disconnected', label: '🔌 Disconnected' },
  { code: 'grief', label: '🖤 Grief / loss' },
  { code: 'just-flat', label: '➖ Just flat' },
] as const;


export const FATIGUE_TYPE_CHIPS = [
  { code: 'physical', label: '💪 Physical tiredness' },
  { code: 'mental', label: '🧠 Mental fatigue' },
  { code: 'bone-tired', label: '🦴 Bone-deep tiredness' },
  { code: 'cant-sleep', label: '😩 Exhausted but can\'t sleep' },
  { code: 'post-pain', label: '🔋 After a pain episode' },
  { code: 'medication', label: '💊 Medication side effect' },
  { code: 'emotional', label: '💔 Emotionally drained' },
] as const;

export const WHAT_HELPED_CATEGORIES = {
  'Physical care': [
    { code: 'heat', label: '🌡 Heat' },
    { code: 'ice', label: '🧊 Ice' },
    { code: 'rest', label: '😴 Rest' },
    { code: 'hydration', label: '💧 Hydration' },
    { code: 'hot-bath', label: '🛁 Hot bath/shower' },
    { code: 'massage', label: '🤲 Massage' },
    { code: 'gentle-walk', label: '🚶 Gentle walk' },
    { code: 'medication', label: '💊 Medication' },
    { code: 'hospital', label: '🏥 Hospital visit' },
  ],
  'Emotional & mental': [
    { code: 'breathing', label: '🌬 Breathing/grounding' },
    { code: 'journaling', label: '📓 Journaling' },
    { code: 'crying', label: '😢 Crying it out' },
    { code: 'talking', label: '🗣 Talking to someone' },
    { code: 'validation', label: '💜 Validation/being heard' },
    { code: 'therapy', label: '🧠 Therapy/counselling' },
    { code: 'prayer', label: '🙏 Prayer/spirituality' },
  ],
  'Social & community': [
    { code: 'peer', label: '🤝 Peer support' },
    { code: 'community', label: '🌐 Community support group' },
    { code: 'distraction', label: '📺 Distraction (TV/music)' },
    { code: 'nature', label: '🌿 Nature/fresh air' },
    { code: 'self-advocacy', label: '✊ Self-advocacy' },
    { code: 'care-plan', label: '📋 Following my care plan' },
  ],
  'Nothing': [
    { code: 'nothing-yet', label: '🚫 Nothing helped yet' },
  ],
} as const;

// Chip selectors are text-only for now (2026-05-21 revert). Group order keeps
// the four-valence + SCD-specific structure from the 2026-05-14 Emotion Map
// redesign so saved data stays stable. New symbol designs are coming in a
// follow-up phase; the MoodChip interface keeps optional moodId /
// facesPosition fields so that future phase can re-introduce typed mappings
// without another schema change.
export const MOOD_CHIP_GROUPS: Record<string, readonly MoodChip[]> = {
  'Emotional pain': [
    { code: 'sad',        label: 'Sad',        iconKey: 'mood-low' },
    { code: 'anxious',    label: 'Anxious',    iconKey: 'mood-anxious' },
    { code: 'frustrated', label: 'Frustrated' },
    { code: 'angry',      label: 'Angry' },
    { code: 'hopeless',   label: 'Hopeless' },
    { code: 'lonely',     label: 'Lonely',     iconKey: 'mood-lonely' },
  ],
  'Flat or disconnected': [
    { code: 'numb',         label: 'Numb' },
    { code: 'disconnected', label: 'Disconnected' },
    { code: 'exhausted',    label: 'Exhausted' },
    { code: 'dull',         label: 'Just getting through it' },
  ],
  'Mixed or uncertain': [
    { code: 'okay',        label: 'Okay' },
    { code: 'complicated', label: 'Complicated' },
    { code: 'unsure',      label: 'Not sure' },
    { code: 'bittersweet', label: 'Bittersweet' },
  ],
  'Positive': [
    { code: 'calm',      label: 'Calm',     iconKey: 'mood-calm' },
    { code: 'hopeful',   label: 'Hopeful',  iconKey: 'mood-hopeful' },
    { code: 'grateful',  label: 'Grateful' },
    { code: 'proud',     label: 'Proud' },
    { code: 'connected', label: 'Connected' },
    { code: 'happy',     label: 'Happy' },
  ],
  'SCD-specific': [
    { code: 'pain-tired', label: 'Pain-tired' },
    { code: 'warrior',    label: 'Warrior mode', iconKey: 'mood-warrior' },
    { code: 'invisible',  label: 'Feeling invisible' },
    { code: 'fighting',   label: 'Fighting hard today' },
  ],
} as const;

export const STIGMA_LAYER_CHIPS = [
  { code: 'cultural', label: 'Beliefs in society', desc: 'Stereotypes, false beliefs about pain and drug-seeking, historical bias' },
  { code: 'structural', label: 'How the system is built', desc: 'Policies, insurance barriers, lack of SCD funding or expertise' },
  { code: 'interpersonal', label: 'How someone treated me', desc: 'Provider bias, dismissal, microaggressions, pain-related discrimination' },
  { code: 'intrapersonal', label: 'How it landed inside me', desc: 'Self-doubt, shame, or stress that stuck after the experience' },
] as const;

export const STIGMA_EXPERIENCE_TYPE_CHIPS = [
  { code: 'enacted', label: 'Something happened', desc: 'Direct — someone said or did something harmful' },
  { code: 'anticipated', label: 'I knew it was coming', desc: 'Expected mistreatment based on past experience' },
  { code: 'perceived', label: 'I could feel it even if nothing was said', desc: 'Sensed bias in tone, body language, or atmosphere' },
  { code: 'internalized', label: 'I started believing it about myself', desc: 'Absorbed the negative message' },
  { code: 'secondary', label: 'It happened to someone with me', desc: 'Witnessed it happen to family or caregiver' },
  { code: 'not-sure', label: 'Still processing', desc: 'Not ready to name it yet — and that\'s okay' },
] as const;

export const STIGMA_ATTRIBUTION_CHIPS = [
  { code: 'race', label: 'Race or ethnicity' },
  { code: 'age', label: 'My age' },
  { code: 'gender', label: 'Gender' },
  { code: 'pain-display', label: 'How I show pain' },
  { code: 'drug-seeking', label: 'Drug-seeking label' },
  { code: 'diagnosis', label: 'My SCD diagnosis' },
  { code: 'appearance', label: 'My appearance' },
  { code: 'insurance', label: 'Insurance / status' },
  { code: 'disability', label: 'Disability' },
  { code: 'culture', label: 'Culture or language' },
  { code: 'sexuality', label: 'Sexuality' },
  { code: 'income', label: 'Income / class' },
  { code: 'system-bias', label: 'System-wide bias' },
  { code: 'unsure', label: 'Not sure' },
] as const;

export const STIGMA_EMOTIONAL_CHIPS = {
  'Internal feelings': [
    { code: 'shame', label: 'Shame' },
    { code: 'self-doubt', label: 'Self-doubt' },
    { code: 'anger', label: 'Anger' },
    { code: 'sadness', label: 'Sadness' },
    { code: 'fear', label: 'Fear' },
    { code: 'humiliated', label: 'Humiliated' },
    { code: 'invisible', label: 'Invisible/erased' },
    { code: 'powerless', label: 'Powerless' },
    { code: 'validated', label: 'Validated' },
    { code: 'grateful', label: 'Grateful' },
  ],
  'Physical response': [
    { code: 'shut-down', label: 'Shut down/froze' },
    { code: 'cried', label: 'Cried' },
    { code: 'heart-raced', label: 'Heart raced' },
    { code: 'trembled', label: 'Trembled/shook' },
    { code: 'left', label: 'Left/walked away' },
    { code: 'numb', label: 'Went numb' },
  ],
} as const;

export const STIGMA_EXPANDED_PROTECTIVE_FACTORS = [
  { code: 'support-person', label: 'Support person with me' },
  { code: 'spoke-up', label: 'I spoke up for myself' },
  { code: 'provider-listened', label: 'A provider listened' },
  { code: 'family', label: 'Family support' },
  { code: 'faith', label: 'Faith/community' },
  { code: 'grounding', label: 'Grounding/breathing helped' },
  { code: 'script', label: 'Used my script or care plan' },
  { code: 'peer-support', label: 'Community/peer support' },
  { code: 'rested', label: 'I rested after' },
  { code: 'humour', label: 'Humour / lightness helped' },
  { code: 'advocate', label: 'Patient advocate present' },
  { code: 'documented', label: 'I documented it' },
  { code: 'rights-knowledge', label: 'Knew my rights' },
  { code: 'self-compassion', label: 'Gave myself compassion' },
] as const;

export const STIGMA_COPING_CHIPS = {
  'In the moment': [
    { code: 'stayed-calm', label: 'Stayed calm' },
    { code: 'asserted', label: 'Asserted myself' },
    { code: 'asked-help', label: 'Asked for help' },
    { code: 'documented', label: 'Documented it' },
    { code: 'left', label: 'Left the situation' },
    { code: 'stayed-silent', label: 'Stayed silent' },
  ],
  'Afterwards': [
    { code: 'talked', label: 'Talked to someone' },
    { code: 'journaled', label: 'Journaled' },
    { code: 'rested', label: 'Rested' },
    { code: 'distracted', label: 'Distracted myself' },
    { code: 'therapy', label: 'Therapy/counselling' },
    { code: 'peer', label: 'Peer support' },
  ],
} as const;

// ════════════════════════════════════════════════════════════════════
// Stigma Tracker (2026-05-14 redesign)
// HSDF (Stangl et al. 2019) + CPHO Action Framework (Tam/PHAC 2019)
// ════════════════════════════════════════════════════════════════════

// Step 1 — How did this make you feel
// HSDF anchor: Manifestations → internalized + Health and Social Impacts (inner half)
// Balanced 4 heavy + Mixed + 4 light
export const STEP1_AFFECT_CHIPS = [
  { code: 'erased', label: 'Erased', valence: 'heavy' as const },
  { code: 'shamed', label: 'Shamed', valence: 'heavy' as const },
  { code: 'powerless', label: 'Powerless', valence: 'heavy' as const },
  { code: 'angry', label: 'Angry', valence: 'heavy' as const },
  { code: 'mixed', label: 'Mixed', valence: 'mixed' as const },
  { code: 'validated', label: 'Validated', valence: 'light' as const },
  { code: 'seen', label: 'Seen', valence: 'light' as const },
  { code: 'cared-for', label: 'Cared for', valence: 'light' as const },
  { code: 'respected', label: 'Respected', valence: 'light' as const },
] as const;

// Step 2 — What someone said or done
// CPHO anchor: Interpersonal + Institutional practices
// HSDF anchor: Manifestations → Stigma Practices (four sub-constructs)
// Five youth-facing groups (CPHO-direct labels); HSDF constructs in design record.
export const STEP2_PRACTICE_GROUPS = [
  {
    code: 'language',
    label: 'Language used against me',
    hsdfConstruct: 'Stereotypes + Prejudice',
    chips: [
      { code: 'slurs', label: 'Slurs' },
      { code: 'misgendered', label: 'Misgendered' },
      { code: 'deadnamed', label: 'Deadnamed' },
      { code: 'drug-seeking', label: 'Drug-seeking accusation' },
      { code: 'dont-look-sick', label: '"You don\'t look sick"' },
      { code: 'demeaning-condition', label: 'Demeaning words about my condition' },
    ],
  },
  {
    code: 'intrusive',
    label: 'Intrusive attention or questions',
    hsdfConstruct: 'Prejudice',
    chips: [
      { code: 'intrusive-questions-body', label: 'Intrusive questions about my body' },
      { code: 'intrusive-questions-condition', label: 'Intrusive questions about my condition' },
      { code: 'intrusive-questions-identity', label: 'Intrusive questions about my identity' },
      { code: 'staring', label: 'Staring' },
      { code: 'curiosity', label: 'Treated me as a curiosity' },
      { code: 'outed', label: 'Outed without consent' },
    ],
  },
  {
    code: 'hostile',
    label: 'Hostile or unsafe treatment',
    hsdfConstruct: 'Prejudice + Stigmatizing behaviour',
    chips: [
      { code: 'verbal-abuse', label: 'Verbal abuse' },
      { code: 'threatened', label: 'Threatened' },
      { code: 'security-called', label: 'Security called' },
      { code: 'watched-followed', label: 'Watched or followed' },
      { code: 'racial-profiling', label: 'Racial profiling' },
      { code: 'harassed', label: 'Harassed' },
    ],
  },
  {
    code: 'less-than',
    label: 'Made to feel "less than"',
    hsdfConstruct: 'Discriminatory attitudes',
    chips: [
      { code: 'talked-down-to', label: 'Talked down to' },
      { code: 'pitied', label: 'Pitied' },
      { code: 'dismissed', label: 'Dismissed' },
      { code: 'ignored', label: 'Ignored' },
      { code: 'not-listened', label: 'Not listened to' },
      { code: 'long-wait', label: 'Long wait after others' },
      { code: 'condition-doubted', label: 'My condition was doubted' },
    ],
  },
  {
    code: 'decisions',
    label: 'Decisions or policies against me',
    hsdfConstruct: 'Stigmatizing behaviour',
    chips: [
      { code: 'medication-refused', label: 'Medication refused' },
      { code: 'denied-care', label: 'Denied care' },
      { code: 'accommodations-refused', label: 'Accommodations refused' },
      { code: 'forced-protocol', label: 'Forced into wrong protocol' },
      { code: 'kicked-out', label: 'Kicked out' },
    ],
  },
] as const;

// Step 3 — What do you think shaped this?
// HSDF anchor: Drivers + Facilitators + Stigma Marking (intersecting stigmas) + socio-ecological levels
// CPHO anchor: Institutional + Population

export const STEP3_SETTING_CHIPS = [
  { code: 'healthcare', label: 'Healthcare' },
  { code: 'school', label: 'School' },
  { code: 'public', label: 'Public space' },
  { code: 'workplace', label: 'Workplace' },
  { code: 'online', label: 'Online' },
  { code: 'home', label: 'Home' },
  { code: 'other', label: 'Other' },
] as const;

export const STEP3_NAMED_FORCES = [
  { code: 'anti-black-racism', label: 'Anti-Black racism' },
  { code: 'racism', label: 'Racism' },
  { code: 'ableism', label: 'Ableism' },
  { code: 'pain-bias', label: 'Pain bias' },
  { code: 'sexism', label: 'Sexism' },
  { code: 'homophobia', label: 'Homophobia' },
  { code: 'transphobia', label: 'Transphobia' },
  { code: 'classism', label: 'Classism' },
  { code: 'ageism', label: 'Ageism' },
  { code: 'xenophobia', label: 'Xenophobia' },
  { code: 'condition-stereotypes', label: 'Condition stereotypes' },
  { code: 'the-system', label: 'The system' },
  { code: 'not-sure', label: 'Not sure yet' },
] as const;

// Depth panel only — surfaced if user opens the depth detail
export const STEP3_NAMED_INTERSECTIONS = [
  { code: 'misogynoir', label: 'Misogynoir' },
  { code: 'transmisogyny', label: 'Transmisogyny' },
  { code: 'racialized-ableism', label: 'Racialized ableism' },
  { code: 'adultism', label: 'Adultism' },
] as const;

export const CONNECTION_FOLLOWUP_CHIPS = [
  { code: 'all-together', label: 'Yes, all together' },
  { code: 'some', label: 'Some of these' },
  { code: 'not-sure', label: 'Not sure' },
] as const;

// Step 4 — What carried you through
// HSDF anchor: Outcomes → Affected Populations (resilience, advocacy, access)
// CPHO anchor: Individual-level intervention ("Strengthening resilience")
// Six categories. Agency chips folded into Emotional and Social/People rather than
// having their own "What I did" column (would imply youth carry responsibility
// for systemic harm). Indigenous community / kin / land deliberately not included —
// pending Indigenous co-design per CareWeave CLAUDE.md Spirit branch note.
export const STEP4_CARRIED_CATEGORIES = [
  {
    code: 'physical',
    label: 'Physical care',
    chips: [
      { code: 'heat', label: 'Heat' },
      { code: 'ice', label: 'Ice' },
      { code: 'rest', label: 'Rest' },
      { code: 'hydration', label: 'Hydration' },
      { code: 'hot-bath', label: 'Hot bath' },
      { code: 'massage', label: 'Massage' },
      { code: 'gentle-walk', label: 'Gentle walk' },
      { code: 'medication', label: 'Medication' },
      { code: 'hospital', label: 'Hospital' },
    ],
  },
  {
    code: 'emotional',
    label: 'Emotional and mental',
    chips: [
      { code: 'breathing', label: 'Breathing' },
      { code: 'journaling', label: 'Journaling' },
      { code: 'crying', label: 'Crying' },
      { code: 'talking', label: 'Talking' },
      { code: 'being-heard', label: 'Being heard' },
      { code: 'therapy', label: 'Therapy' },
      { code: 'prayer', label: 'Prayer' },
      { code: 'self-compassion', label: 'Giving myself compassion' },
      { code: 'asking-help', label: 'Asking for help' },
      { code: 'care-plan', label: 'Using my care plan' },
      { code: 'knowing-rights', label: 'Knowing my rights' },
    ],
  },
  {
    code: 'people',
    label: 'People who showed up',
    chips: [
      { code: 'family', label: 'Family' },
      { code: 'friend', label: 'Friend' },
      { code: 'peer', label: 'Peer' },
      { code: 'stranger', label: 'A stranger helped' },
      { code: 'advocate', label: 'Someone advocated for me' },
    ],
  },
  {
    code: 'communities',
    label: 'Communities that hold me',
    chips: [
      { code: 'community-gets-it', label: 'Community that gets it' },
      { code: 'chosen-family', label: 'Chosen family' },
      { code: 'elders', label: 'Elders' },
      { code: 'faith', label: 'Faith community' },
      { code: 'disability-justice', label: 'Disability or chronic illness community' },
      { code: '2slgbtqia', label: '2SLGBTQIA+ community' },
      { code: 'black-community', label: 'Black community or community of colour' },
      { code: 'online-community', label: 'Online community of people like me' },
    ],
  },
  {
    code: 'other',
    label: 'Other',
    chips: [
      { code: 'distraction', label: 'Distraction' },
      { code: 'nature', label: 'Nature' },
    ],
  },
  {
    code: 'nothing',
    label: 'Nothing',
    chips: [
      { code: 'nothing-yet', label: 'Nothing helped yet' },
    ],
  },
] as const;

// "When" options for Step 2
export const STEP2_WHEN_OPTIONS = [
  { code: 'today', label: 'Today' },
  { code: 'yesterday', label: 'Yesterday' },
  { code: 'this_week', label: 'This week' },
  { code: 'longer_ago', label: 'Longer ago' },
] as const;

// People options for Step 2 (multi-select, unfiltered at this stage)
export const STEP2_PEOPLE_OPTIONS = [
  'Provider', 'Nurse', 'Front desk', 'Security', 'Teacher', 'Classmate',
  'Admin', 'Counsellor', 'Coach', 'Family member', 'Caregiver',
  'Stranger', 'Coworker', 'Manager', 'HR', 'Police', 'Store staff',
  'Online stranger', 'Online community member', 'Other',
] as const;
